import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  FiChevronLeft,
  FiChevronRight,
  FiExternalLink,
  FiVolume2,
  FiRefreshCw,
} from 'react-icons/fi';
import PageLayout from '../../components/layout/PageLayout';
import SEO from '../../components/common/SEO';
import { useUserStore } from '../../stores/userStore';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { AccountBoundary, useSettings } from '../settings/shared';
import { useMemberEntry } from '../members/memberMemory';
import { restoreSearchScroll } from '../search/useSearchScrollMemory';
import { useEvents, visibleEvents } from '../events/useEvents';
import { eventHref, loadNotices, noticeDate, noticePage, noticeTab, uniqueNotices } from './model';
import './ArticleList.css';

export default function ArticleList() {
  return <AccountBoundary component={ArticleBoundary} />;
}
function ArticleBoundary({ user }) {
  const { locale } = useWebLocale();
  const { entry, returning, location } = useMemberEntry(`articles:${locale}`);
  return (
    <ArticleContent
      key={`${location.key}:${entry.version}:${locale}`}
      {...{ user, locale, entry, returning, location }}
    />
  );
}
function State({ title, description, error, retry, en }) {
  return (
    <div className="pp-articles-state" role={error ? 'alert' : 'status'}>
      <p>{title}</p>
      {description && <p>{description}</p>}
      {retry && <button onClick={retry}>{en ? 'Try again' : '다시 시도'}</button>}
    </div>
  );
}
export function NoticeRow({ item, locale, remember }) {
  const [failed, setFailed] = useState(false);
  return (
    <Link
      className="pp-notice-row"
      data-notice-id={item.id}
      to={`/notice/${encodeURIComponent(item.slug || String(item.id))}`}
      onClick={() => remember(item.id)}
    >
      <span className="pp-notice-art" aria-hidden="true">
        {item.imageUrl && !failed ? (
          <img src={item.imageUrl} alt="" onError={() => setFailed(true)} />
        ) : (
          <FiVolume2 size={22} />
        )}
      </span>
      <span className="pp-notice-copy">
        <span className="pp-notice-title">{item.title}</span>
        <span className="pp-notice-date">{noticeDate(item.createdAt, locale)}</span>
      </span>
      <FiChevronRight className="pp-notice-chevron" size={18} aria-hidden="true" />
    </Link>
  );
}
export function NoticeEvent({ item, en }) {
  const href = eventHref(item.destinationUrl),
    [error, setError] = useState(false);
  const title = String(item.title || '').trim(),
    description = String(item.description || '').trim();
  const body = (
    <>
      <span
        className="pp-notice-event-image"
        style={{
          backgroundImage: item.imageUrl ? `url(${JSON.stringify(item.imageUrl)})` : undefined,
        }}
      >
        {(title || description) && <span className="pp-notice-event-shade" />}
        {item.showAdMark && <span className="pp-notice-event-ad">{en ? 'AD' : '광고'}</span>}
        <span className="pp-notice-event-copy">
          {title && <strong>{title}</strong>}
          {description && <span>{description}</span>}
        </span>
      </span>
      <span className="pp-notice-event-action">
        <span>{item.actionLabel}</span>
        <FiExternalLink size={16} aria-hidden="true" />
      </span>
    </>
  );
  const label = [title, description, item.actionLabel].filter(Boolean).join('. ');
  return (
    <div>
      {href ? (
        <a
          className="pp-notice-event"
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${label} · ${en ? 'Opens in a new tab' : '새 탭에서 열기'}`}
        >
          {body}
        </a>
      ) : (
        <button className="pp-notice-event" aria-label={label} onClick={() => setError(true)}>
          {body}
        </button>
      )}
      {error && (
        <p role="alert">
          {en
            ? 'We could not open the application page. Please try again.'
            : '응모 페이지를 열 수 없습니다. 잠시 후 다시 시도해주세요.'}
        </p>
      )}
    </div>
  );
}
function ArticleContent({ user, locale, entry, returning, location }) {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams(),
    tab = noticeTab(params),
    en = locale === 'en';
  const { style } = useSettings(),
    admin = useUserStore((s) => s.isAdmin);
  const [expanded, setExpanded] = useState(
    () => parseFloat(getComputedStyle(document.documentElement).fontSize) >= 22.4,
  );
  useEffect(() => {
    const update = () =>
      setExpanded(parseFloat(getComputedStyle(document.documentElement).fontSize) >= 22.4);
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    window.addEventListener('resize', update);
    update();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);
  const initial = noticePage(params),
    more = useRef(null),
    restore = useRef(returning);
  const list = useInfiniteQuery({
    queryKey: [
      'notice-list60',
      locale,
      user?.id || 'guest',
      entry.version,
      location.state?.articleVisit || location.key,
      initial,
    ],
    queryFn: ({ pageParam, signal }) => loadNotices(pageParam, locale, signal),
    initialPageParam: initial,
    getNextPageParam: (page) => page.next,
    enabled: tab === 'notice',
    retry: false,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
  const events = useEvents(),
    active = tab === 'event' ? events : list;
  const items = uniqueNotices(list.data?.pages.flatMap((p) => p.items) || []);
  const ready = !active.isPending;
  useEffect(() => {
    if (!restore.current || !ready) return;
    const timer = setTimeout(() => {
      if (!entry.current()) return;
      cancel = restoreSearchScroll(entry.y || 0, () => {
        restore.current = false;
        if (entry.current() && entry.focus != null)
          document
            .querySelector(`[data-notice-id="${Number(entry.focus)}"]`)
            ?.focus({ preventScroll: true });
      });
    }, 0);
    let cancel;
    return () => {
      clearTimeout(timer);
      cancel?.();
    };
  }, [entry, ready]);
  useEffect(() => {
    const save = () => {
      if (entry.current() && !restore.current) entry.y = window.scrollY;
    };
    window.addEventListener('scroll', save, { passive: true });
    return () => window.removeEventListener('scroll', save);
  }, [entry]);
  const { hasNextPage, isFetching, isError, fetchNextPage } = list;
  useEffect(() => {
    if (
      tab !== 'notice' ||
      !more.current ||
      !hasNextPage ||
      isFetching ||
      isError ||
      typeof IntersectionObserver === 'undefined'
    )
      return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !document.hidden && !restore.current)
          void fetchNextPage({ cancelRefetch: false });
      },
      { rootMargin: '160px' },
    );
    observer.observe(more.current);
    return () => observer.disconnect();
  }, [tab, hasNextPage, isFetching, isError, fetchNextPage]);
  const title = en ? 'Events & Notices' : '이벤트 및 공지사항';
  useEffect(() => {
    if (location.state?.articleTabFocus)
      document.getElementById(`notice-tab-${location.state.articleTabFocus}`)?.focus();
  }, [location]);
  const changeTab = (value, keyboard = false) => {
    const next = new URLSearchParams(params);
    next.set('category', value);
    next.delete('page');
    next.delete('manage');
    setParams(next, {
      state: {
        articleVisit: location.state?.articleVisit || location.key,
        articleTabFocus: keyboard ? value : null,
      },
    });
  };
  const keyTab = (e, value) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const next =
      e.key === 'Home'
        ? 'event'
        : e.key === 'End'
          ? 'notice'
          : value === 'event'
            ? 'notice'
            : 'event';
    document.getElementById(`notice-tab-${next}`)?.focus();
    changeTab(next, true);
  };
  return (
    <PageLayout paddingTop={0}>
      <section
        className={`pp-articles pp-settings${expanded ? ' pp-articles-expanded' : ''}`}
        style={style}
      >
        <SEO title={title} />
        <header className="pp-settings-header">
          <button
            aria-label={en ? 'Back' : '뒤로가기'}
            onClick={() =>
              window.history.state?.idx > 0 ? navigate(-1) : navigate('/mypage/settings')
            }
          >
            <FiChevronLeft size={22} />
          </button>
          <h1>{title}</h1>
          <button
            className="pp-articles-refresh"
            disabled={active.isFetching}
            onClick={() => active.refetch()}
            aria-label={en ? 'Refresh list' : '목록 새로고침'}
          >
            <FiRefreshCw size={16} />
          </button>
        </header>
        <div role="tablist" aria-label={title} className="pp-notice-tabs">
          {['event', 'notice'].map((value) => (
            <button
              key={value}
              role="tab"
              id={`notice-tab-${value}`}
              aria-controls={`notice-panel-${value}`}
              aria-selected={tab === value}
              tabIndex={tab === value ? 0 : -1}
              onKeyDown={(e) => keyTab(e, value)}
              onClick={() => changeTab(value)}
            >
              {value === 'event' ? (en ? 'Events' : '이벤트') : en ? 'Notices' : '공지사항'}
            </button>
          ))}
        </div>
        {admin && (
          <nav className="pp-articles-admin" aria-label={en ? 'Notice management' : '공지 관리'}>
            <Link to="/notice/create">{en ? 'Create notice' : '공지 작성'}</Link>
            <Link to="/notice?manage=hidden">{en ? 'Hidden notices' : '숨긴 공지 관리'}</Link>
          </nav>
        )}
        {/* Separate targets prevent Safari selecting both tabs when restored content has focus. */}
        <div
          role="tabpanel"
          id={`notice-panel-${tab === 'event' ? 'notice' : 'event'}`}
          aria-labelledby={`notice-tab-${tab === 'event' ? 'notice' : 'event'}`}
          hidden
        />
        <div role="tabpanel" id={`notice-panel-${tab}`} aria-labelledby={`notice-tab-${tab}`}>
          {active.isPending && <State en={en} title={en ? 'Loading' : '불러오는 중'} />}
          {tab === 'event' ? (
            <>
              {events.isError && (
                <State
                  en={en}
                  error
                  title={
                    en ? 'We could not load the events.' : '이벤트 목록을 불러오지 못했습니다.'
                  }
                  retry={() => events.refetch()}
                />
              )}
              <div className="pp-notice-events">
                {visibleEvents(events.data).map((item) => (
                  <NoticeEvent key={item.id} {...{ item, en }} />
                ))}
              </div>
              {!events.isPending && !events.isError && !visibleEvents(events.data).length && (
                <State
                  en={en}
                  title={en ? 'No events yet' : '등록된 이벤트가 없습니다'}
                  description={
                    en
                      ? 'New events will appear here when they are ready.'
                      : '새로운 이벤트가 준비되면 이곳에서 알려드릴게요.'
                  }
                />
              )}
            </>
          ) : (
            <>
              <div className="pp-notice-rows" data-more={list.hasNextPage || undefined}>
                {items.map((item) => (
                  <NoticeRow
                    key={item.id}
                    {...{ item, locale }}
                    remember={(id) => {
                      entry.y = window.scrollY;
                      entry.focus = id;
                    }}
                  />
                ))}
              </div>
              {list.isError && (
                <State
                  en={en}
                  error
                  title={en ? 'We could not load the notices.' : '공지 목록을 불러오지 못했습니다.'}
                  retry={() =>
                    list.isFetchNextPageError
                      ? list.fetchNextPage({ cancelRefetch: false })
                      : list.refetch()
                  }
                />
              )}
              {!list.isPending && !list.isError && !items.length && (
                <State
                  en={en}
                  title={en ? 'No notices yet' : '등록된 공지가 없습니다'}
                  description={
                    en
                      ? 'New updates will appear here.'
                      : '새로운 안내가 등록되면 이곳에 표시됩니다.'
                  }
                />
              )}
              {list.hasNextPage && (
                <div ref={more} className="pp-notice-more">
                  <button
                    disabled={list.isFetching}
                    onClick={() => list.fetchNextPage({ cancelRefetch: false })}
                  >
                    {list.isFetching ? (en ? 'Loading' : '불러오는 중') : en ? 'More' : '더 보기'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </PageLayout>
  );
}
